/* W23(b) — THE WORKOUT LIBRARY'S SORT HEADS.
 *
 * `docs/EVERFIT-MAP.md` §18-ter depth 1, read live: their library's column head is FIVE sort
 * controls — `☐ · Workouts (11) ▾ · Tags · Exercises · Most Recent ▾ · Owner` — exactly ONE of
 * them active (blue), the rest grey, and a second press on the active one reverses it. Ours had
 * **no head row at all** and the list came back in whatever order the query gave it
 * (`.order("name")`, which is a default nobody could see, change or link to).
 *
 * ⚠️ **THE MECHANISM WAS ALREADY HERE AND ONLY THE HEAD WAS MISSING** — `useQuerySort`,
 * `sortOrder.ts`'s `nullsLast`/`flip` (its `nextSortOf` went at W72 step 7 with the last header that clicked it), and the exercises library's own sortable
 * table (§6.7, W8). So this file states WHAT the workouts list sorts by and reuses every rule
 * that already exists; nothing about ranking is re-decided here.
 *
 * 🔴 **THREE HEADS, NOT FIVE, AND EACH ABSENCE HAS A REASON RATHER THAN A SHRUG.**
 *   * `Tags` — **nothing in our model carries a workout tag** (W23(c) is that schema question),
 *     and theirs reads `--` on every row of his account. §7's standing bound is that a concept we
 *     have no referent for is ABSENT, never stubbed; a head that sorts a column of dashes is the
 *     "extra bs" his own web rule names, and it is the thing that reads amateur in theirs.
 *   * `Owner` — every template in this library is `client_id is null` and belongs to the one
 *     coach reading the screen. A column whose every cell holds the same value cannot order
 *     anything. It becomes a real question the day a shared library exists, and not before.
 *   * The `☐` CHECKBOX — theirs selects rows, and selection's ONLY visible consumer is the `Tags`
 *     popover's `Apply` (§18-ter depth 3, measured: no bulk bar appears anywhere). With no tags
 *     there is nothing for a selection to do, and a control that does nothing is exactly the
 *     defect W22(g) recorded on this product.
 *
 * ⚠️ **AND THE THIRD HEAD IS SPELLED OURS, DELIBERATELY.** Theirs says `Most Recent`; the
 * exercises library next door already says `Last used` (`exerciseTable.columnLabel`). Two
 * spellings of one concept on two library screens of one product is this repo's defining bug
 * class with a column head in it — so the COPY is "the age is a sortable column", and the word
 * stays the one we already use. The same argument does NOT apply to `Workouts (N)`, which is
 * theirs verbatim because it is the name of the list.
 */
import { byName, flip, nullsLast, numeric, type SortDir } from "./sortOrder";

export type { SortDir };

/** Only the facts a row DRAWS — a head that sorts by something invisible cannot be read. */
export type SortableWorkout = {
  name: string;
  /** How many exercise rows the template prescribes. Always a number; 0 is a real answer. */
  exercises: number;
  /** ISO date the template was last assigned for, or null for never. */
  lastUsedISO: string | null;
};

export const workoutSortKeys = ["name", "exercises", "lastUsed"] as const;
export type WorkoutSortKey = (typeof workoutSortKeys)[number];

/**
 * Each head's DEFAULT direction — the question a coach is asking when they press it.
 *
 * ⚠️ **`exercises` LEADS WITH THE BIGGEST, AND THAT IS MEASURED AGAINST HIS OWN LIBRARY RATHER
 * THAN REASONED IN THE ABSTRACT.** Five of his templates are one-row GTG tasks (`Daily handstand`,
 * `GTG FL Touch Holds`, `GTG tuck FL touch`, `GTG tuck planche`, …). Ascending would open the
 * column with a wall of `1`s — the rows a coach pressing a SIZE head is least often looking for —
 * and push every real session below the fold. This is `exerciseTable`'s own argument, which gives
 * `records` the opposite default for the opposite reason: there the empty rows ARE the finding.
 *
 * ⚠️ `lastUsed` LEADS WITH THE OLDEST, which is `exerciseTable.defaultDir.lastUsed` unchanged:
 * "what did I use yesterday" is not a question anybody has; "what have I stopped reaching for" is.
 */
export const workoutDefaultDir: Record<WorkoutSortKey, SortDir> = {
  name: "asc",
  exercises: "desc",
  lastUsed: "asc",
};

/**
 * A stable, total order for one column. Absent values sort LAST whichever way the column runs.
 *
 * ⚠️ **NEVER ASSIGNED IS ABSENT, NOT OLD.** A template nobody has ever used has no date, and
 * giving it one — ancient under *oldest first*, or nothing under *newest first* — answers a
 * question the row cannot answer. `sortOrder.nullsLast` is the one rule for that and it is NOT
 * multiplied by the direction, which is the single mistake that file exists to prevent.
 */
export function compareWorkouts(
  a: SortableWorkout, b: SortableWorkout, key: WorkoutSortKey, dir: SortDir,
): number {
  const f = flip(dir);
  switch (key) {
    case "name":
      return byName(a.name, b.name) * f;
    case "exercises":
      /* No absent case: a template with no exercises has ZERO of them, which is a fact and not a
         gap — and it is the one a coach most wants to find when the column runs ascending. */
      return numeric(a.exercises, b.exercises) * f;
    case "lastUsed":
      if (a.lastUsedISO === null || b.lastUsedISO === null) {
        return nullsLast(a.lastUsedISO, b.lastUsedISO, byName);
      }
      /* ISO dates compare as text — `byName` rather than a `Date` round trip, which is what the
         exercises table already does with the same column. */
      return byName(a.lastUsedISO, b.lastUsedISO) * f;
  }
}

/**
 * Sort a list by one column. NOT in place, and STABLE within equal keys.
 *
 * ⚠️ **THE TIE-BREAK MATTERS HERE MORE THAN IT DOES NEXT DOOR.** Five of his templates hold one
 * exercise and eight have never been assigned, so the `exercises` and `lastUsed` columns are
 * mostly ties — and `Array.prototype.sort` is stable by specification, which means the list keeps
 * the NAME order the query returned it in. A press of a head therefore never reshuffles rows it
 * has nothing to say about.
 */
export function sortWorkouts<T extends SortableWorkout>(
  rows: readonly T[], key: WorkoutSortKey, dir: SortDir,
): T[] {
  return [...rows].sort((a, b) => compareWorkouts(a, b, key, dir));
}

/**
 * Read a sort out of a route query, refusing anything it does not recognise.
 *
 * ⚠️ AN UNRECOGNISED DIRECTION FALLS TO THE **COLUMN'S** OWN DEFAULT, never to `asc` —
 * `?sort=exercises&dir=banana` must not quietly open with the one-row tasks. Same rule, same
 * wording, as `exerciseTable.sortFromQuery`; this list simply has its own keys.
 */
export function workoutSortFromQuery(
  q: Record<string, string>,
): { key: WorkoutSortKey; dir: SortDir } {
  const key = workoutSortKeys.find((k) => k === q.sort);
  /* W72 STEP 5 — WITH NO SORT IN THE ADDRESS THE LIBRARY OPENS ON LAST USED, MOST RECENT FIRST (Proposal 5: "sorted
     by last used"). It was `name`; a pasted `?sort=name` still opens by name. */
  if (key === undefined) return { key: "lastUsed", dir: "desc" };
  const dir: SortDir = q.dir === "asc" || q.dir === "desc" ? q.dir : workoutDefaultDir[key];
  return { key, dir };
}

/**
 * The query string for a sort — the ONE place these two keys are spelled on this screen.
 *
 * ⚠️ **THE SCREEN'S OWN DEFAULT IS STILL WRITTEN OUT**, unlike `libraryQuery`'s `video_only`.
 * That one omits its default because `false` is the absence of a filter; this one has no absence
 * — a list is always in SOME order — and a link that carries the order it is showing is the whole
 * point of putting it in the address.
 */
export function workoutSortQuery(sort: { key: WorkoutSortKey; dir: SortDir }): string {
  return `?${new URLSearchParams({ sort: sort.key, dir: sort.dir }).toString()}`;
}

/* `workoutColumnLabel`, `nextWorkoutSort` and `sortStatePhrase` — the W23(b) sort HEADS' words, press rule and
 * accessible phrase — retired in W72 step 5 with the heads: the Library's one sort control is a `<select>` whose value
 * is this address (Proposal 5). */
